import { io } from "socket.io-client";

// Constants for socket configuration
const GAME_URL= process.env.REACT_APP_GAME_SERVER_URL;
const LOCAL_URL = 'http://localhost';

// Determine URL based on environment
const URL = process.env.REACT_APP_JOSHUA === "true" 
  ? LOCAL_URL 
  : GAME_URL;

// Create socket with improved configuration for stable connections
const socket = io(URL, {
  autoConnect: false,
  transports: ['websocket', 'polling'], // Try WebSocket first, fall back to polling
  reconnection: true,
  reconnectionAttempts: Infinity, // Never stop trying to reconnect
  reconnectionDelay: 1000, // Start with 1 second delay
  reconnectionDelayMax: 5000, // Maximum 5 second delay
  timeout: 20000, // 20 second connection timeout
  pingInterval: 25000, // Send a ping every 25 seconds
  pingTimeout: 30000 // Consider connection closed if no pong for 30 seconds
});

// Keep track of connection state and user data
let isConnected = false;
let userData = null;
let heartbeatInterval = null;
let reconnectTimeout = null;
let reconnectAttempts = 0;

// Function to get auth token
const getAuthToken = () => {
  try {
    const tokenStr = sessionStorage.getItem("token");
    if (tokenStr) {
      const parsedToken = JSON.parse(tokenStr);
      return parsedToken.accessToken;
    }
  } catch (error) {
    // console.error("Error getting auth token:", error);
  }
  return null;
};

// Setup core socket event handlers
socket.on("connect", () => {
  // console.log("Socket connected with ID:", socket.id);
  isConnected = true;
  reconnectAttempts = 0;
  
  // Register as active user after connection
  if (userData) {
    // console.log("Registering as active user:", userData);
    socket.emit("user-active", userData);
    
    // Request active users
    setTimeout(() => {
      socket.emit("get_active_users");
    }, 1000);
  }
  
  // Start heartbeat
  startHeartbeat();
});

socket.on("disconnect", (reason) => {
  // console.log("Socket disconnected, reason:", reason);
  isConnected = false;
  
  // Clear heartbeat
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
  
  // For certain disconnect reasons, try to reconnect immediately
  if (reason === "io server disconnect" || reason === "ping timeout" || reason === "transport close") {
    reconnectAttempts++;
    
    // Clear any existing reconnect timeout
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }
    
    // Exponential backoff for reconnect (1s, 2s, 4s, max 8s)
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 8000);
    
    reconnectTimeout = setTimeout(() => {
      // console.log(`Attempting reconnection (#${reconnectAttempts})...`);
      socket.connect();
    }, delay);
  }
});

socket.on("connect_error", (error) => {
  // console.error("Socket connection error:", error);
  isConnected = false;
  
  // Try polling if websocket fails repeatedly
  if (reconnectAttempts > 2 && socket.io.opts.transports[0] === 'websocket') {
    // console.log("WebSocket connection failing, trying polling transport...");
    socket.io.opts.transports = ['polling', 'websocket'];
  }
  
  reconnectAttempts++;
});

// Keep-alive heartbeat function
const startHeartbeat = () => {
  // Clear any existing interval
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }
  
  // Send a heartbeat every 25 seconds
  heartbeatInterval = setInterval(() => {
    if (isConnected && userData) {
      // console.log("Sending heartbeat and refreshing active status");
      socket.emit("heartbeat", userData);
      socket.emit("user-active", userData); // Re-register as active with each heartbeat
    }
  }, 25000);
};

// Socket manager object with enhanced functionality
const socketManager = {
  socket,
  
  // Connect with user data
  connect: (userInfo) => {
    if (!userInfo || !userInfo.userId) {
      // console.error("Invalid user data provided:", userInfo);
      return socket;
    }
    
    // Store user data for reconnects
    userData = userInfo;
    
    // Set auth data for connection
    socket.auth = { 
      ...userData,
      token: getAuthToken() ? `Bearer ${getAuthToken()}` : undefined
    };
    
    // console.log("Connecting socket with user data:", userData);
    
    // Connect if not already connected
    if (!socket.connected) {
      socket.connect();
    } else {
      // If already connected, just update user status
      socket.emit("user-active", userData);
      
      // Refresh heartbeat
      startHeartbeat();
    }
    
    return socket;
  },
  
  // Explicitly disconnect socket
  disconnect: () => {
    // Clean up intervals
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    
    // Disconnect socket
    if (socket.connected) {
      socket.disconnect();
    }
    
    isConnected = false;
    // console.log("Socket manually disconnected");
  },
  
  // Check if socket is connected
  isConnected: () => isConnected,
  
  // Register user as active
  registerActiveUser: () => {
    if (isConnected && userData) {
      // console.log("Explicitly registering as active user");
      socket.emit("user-active", userData);
      return true;
    }
    return false;
  },
  
  // Request active users list
  requestActiveUsers: () => {
    if (isConnected) {
      // console.log("Requesting active users list");
      socket.emit("get_active_users");
      return true;
    }
    return false;
  },
  
  // Get the current user data
  getUserData: () => userData,
  
  // Get socket ID
  getSocketId: () => socket.id,
  
  // Update user data
  updateUserData: (newData) => {
    userData = { ...userData, ...newData };
    if (isConnected) {
      socket.emit("user-active", userData);
    }
    return userData;
  }
};

export default socketManager;